import { useContext, useCallback, useMemo } from 'react';
import styled, { useTheme } from 'foundations-theming/utils';
import { getPrimaryButtonStyle, getPrimaryButtonHoverStyle, getSecondaryButtonStyle, getSecondaryButtonHoverStyle } from 'MeetingsLib/utils/color';
import PreviewContext from 'MeetingsLib/utils/PreviewContext';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Box } from 'foundations-components/transitional/components/Box';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { View } from 'foundations-components/components/View';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BUTTON_WIDTH = '102px';
// Styled wrapper that targets the Button component inside it
const ButtonWrapper = styled.div.withConfig({
  displayName: "StepButtonsFooter__ButtonWrapper"
})(["> button{width:", ";visibility:", ";background-color:", " !important;border-color:", " !important;color:", " !important;", " ", "}"], BUTTON_WIDTH, props => props.visibility, props => props.customStyle.backgroundColor, props => props.customStyle.borderColor, props => props.customStyle.color, props => !props.disabled && `
      &:hover {
        background-color: ${props.customHoverStyle.backgroundColor} !important;
        border-color: ${props.customHoverStyle.borderColor} !important;
        color: ${props.customHoverStyle.color} !important;
      }
    `, props => props.disabled && `
      opacity: 0.50;
    `);
const StepButtonsFooter = ({
  submittingMeeting,
  goToPreviousStep,
  goToNextStep,
  isLastStep,
  canProceed,
  disableBackButton,
  backButtonTooltipTitle,
  offline,
  tooltipTitle,
  compact,
  branding
}) => {
  const theme = useTheme();
  const isPreview = useContext(PreviewContext);
  const forwardButtonKey = useMemo(() => {
    const lastStepKey = offline ? 'buttons.confirm.offline' : 'buttons.confirm.online';
    return isLastStep ? lastStepKey : 'buttons.next';
  }, [isLastStep, offline]);
  const forwardButtonVisibility = useMemo(() => {
    return goToNextStep ? 'visible' : 'hidden';
  }, [goToNextStep]);
  const getBackFunction = useCallback(() => {
    if (isPreview && (!compact || isLastStep !== undefined)) return;
    if (goToPreviousStep) {
      goToPreviousStep();
    }
  }, [isPreview, compact, isLastStep, goToPreviousStep]);
  const proceedFunction = useCallback(() => {
    if (goToNextStep && !isPreview) {
      goToNextStep();
    }
  }, [goToNextStep, isPreview]);
  const renderBackButton = useCallback(() => {
    if (!goToPreviousStep) {
      return null;
    }
    const backButton =
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized we need to use a wrapper div to apply custom branding styles to the button */
    _jsx(ButtonWrapper, {
      customHoverStyle: getSecondaryButtonHoverStyle(branding.primaryColor, theme),
      customStyle: getSecondaryButtonStyle(branding.primaryColor, theme),
      disabled: disableBackButton,
      visibility: "visible",
      children: /*#__PURE__*/_jsxs(Button, {
        marginInlineStart: "0",
        onClick: getBackFunction,
        disabled: disableBackButton,
        use: "secondary",
        size: compact ? 'small' : 'default',
        responsive: false,
        "data-test-id": "back-btn",
        "data-selenium-test": "back-button",
        children: [/*#__PURE__*/_jsx(LeftIcon, {
          color: "inherit"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "buttons.back"
        })]
      })
    });
    if (backButtonTooltipTitle) {
      return /*#__PURE__*/_jsx(Tooltip, {
        title: backButtonTooltipTitle,
        disabled: !disableBackButton,
        children: backButton
      });
    }
    return backButton;
  }, [goToPreviousStep, branding.primaryColor, disableBackButton, getBackFunction, compact, backButtonTooltipTitle, theme]);
  const renderForwardButton = useCallback(() => {
    const forwardButton =
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized we need to use a wrapper div to apply custom branding styles */
    _jsx(ButtonWrapper, {
      customHoverStyle: getPrimaryButtonHoverStyle(branding.primaryColor, theme),
      customStyle: getPrimaryButtonStyle(branding.primaryColor, theme),
      disabled: !canProceed,
      visibility: forwardButtonVisibility,
      children: /*#__PURE__*/_jsx(LoadingButton, {
        "data-test-id": "forward-btn",
        loading: submittingMeeting || false,
        onClick: proceedFunction,
        use: "primary",
        size: compact ? 'small' : 'medium',
        responsive: false,
        disabled: !canProceed,
        "data-selenium-test": "forward-button",
        paddingInline: "0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: forwardButtonKey
        })
      })
    });
    if (tooltipTitle) {
      return /*#__PURE__*/_jsx(Tooltip, {
        title: tooltipTitle,
        disabled: canProceed,
        children: /*#__PURE__*/_jsx("span", {
          children: forwardButton
        })
      });
    }
    return forwardButton;
  }, [forwardButtonVisibility, forwardButtonKey, branding.primaryColor, canProceed, submittingMeeting, proceedFunction, compact, tooltipTitle, theme]);
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-surface-recessed",
    marginBlockStart: "400",
    children: /*#__PURE__*/_jsx(Box, {
      grow: 0,
      paddingInline: "600",
      paddingBlock: compact ? '400' : '600',
      children: /*#__PURE__*/_jsxs(TFlex, {
        direction: "row-reverse",
        align: "end",
        justify: "between",
        children: [renderForwardButton(), renderBackButton()]
      })
    })
  });
};
export default StepButtonsFooter;